/**
 * GenerateFromTemplateModal - Gerar receita a partir de um template
 * Fluxo: Selecionar Template -> Inserir dados do paciente -> Imprimir
 */

import { useState, useEffect } from 'react';
import axiosInstance from '@/utils/axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Printer, User, Pill, FileText, ArrowLeft, Search } from 'lucide-react';
import { printPrescriptions } from '@/services/printService';
import PatientSearchAutocomplete from './PatientSearchAutocomplete';

export default function GenerateFromTemplateModal({ open, onClose, template: initialTemplate = null }) {
  const [step, setStep] = useState(initialTemplate ? 'patient' : 'select');
  const [templates, setTemplates] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(initialTemplate);
  
  // Patient data
  const [patientName, setPatientName] = useState('');
  const [patientCPF, setPatientCPF] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientSearch, setPatientSearch] = useState('');

  useEffect(() => {
    if (open) {
      if (initialTemplate) {
        setSelectedTemplate(initialTemplate);
        setStep('patient');
      } else {
        setStep('select');
        fetchTemplates();
      }
    } else {
      resetForm();
    }
  }, [open, initialTemplate]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (open && step === 'select') fetchTemplates();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/prescription-templates/', {
        params: { search: searchQuery || undefined }
      });
      setTemplates(response.data);
    } catch (error) {
      console.error('Erro ao buscar templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setPatientName('');
    setPatientCPF('');
    setSelectedPatient(null);
    setPatientSearch('');
    setSelectedTemplate(initialTemplate);
    setSearchQuery('');
  };

  const handleSelectTemplate = (tmpl) => {
    setSelectedTemplate(tmpl);
    setStep('patient');
  };

  const handleSelectPatient = (patient) => {
    setSelectedPatient(patient);
    setPatientName(patient.nome || '');
    setPatientCPF(patient.cpf || '');
  };

  const formatCPF = (value) => {
    const cleaned = value.replace(/\D/g, '').slice(0, 11);
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6) return `${cleaned.slice(0, 3)}.${cleaned.slice(3)}`;
    if (cleaned.length <= 9) return `${cleaned.slice(0, 3)}.${cleaned.slice(3, 6)}.${cleaned.slice(6)}`;
    return `${cleaned.slice(0, 3)}.${cleaned.slice(3, 6)}.${cleaned.slice(6, 9)}-${cleaned.slice(9)}`;
  };

  const handlePrint = async () => {
    if (!patientName.trim()) {
      toast.error('Informe o nome do paciente');
      return;
    }
    if (!selectedTemplate?.medications?.length) {
      toast.error('Template sem medicamentos');
      return;
    }

    // Register template usage
    try {
      await axiosInstance.post(`/prescription-templates/${selectedTemplate.id}/use`);
    } catch (e) { /* ignore */ }

    // Build prescription object from template
    const meds = selectedTemplate.medications.map(med => ({
      name: med.name,
      dosage: med.dosage,
      dosage_unit: med.dosage_unit || 'MG',
      instructions: med.instructions || med.frequency || '',
      quantity: med.quantity,
      packaging_type: med.packaging_type,
      via: med.route ? `USO ${med.route}` : 'USO ORAL',
    }));

    // Group by via
    const groups = {};
    for (const med of meds) {
      const via = med.via || 'USO ORAL';
      if (!groups[via]) groups[via] = [];
      groups[via].push(med);
    }
    const medicationGroups = Object.entries(groups).map(([via, medicamentos]) => ({ via, medicamentos }));

    const now = Date.now();
    const prescription = {
      id: `template-gen-${now}`,
      name: selectedTemplate.template_name,
      medicamentos: meds,
      medication_groups: medicationGroups,
      uso: Object.keys(groups).join(' / '),
      data_criacao: new Date(now).toISOString(),
      data_impressao: new Date(now).toISOString(),
      data_validade: new Date(now + 60 * 24 * 60 * 60 * 1000).toISOString(),
    };

    const patient = {
      id: selectedPatient?.id || `avulso-${now}`,
      nome: patientName.toUpperCase(),
      cpf: patientCPF || undefined,
    };

    // Log to print history if it's a registered patient
    if (selectedPatient?.id) {
      try {
        // Try to create a quick stat for tracking
        await axiosInstance.post('/quick-prescriptions/stats', {
          patient_name: patientName,
          medications: meds.map(m => ({ name: m.name, dosage: m.dosage, dosage_unit: m.dosage_unit })),
          via: prescription.uso,
          created_at: new Date().toISOString(),
          created_by: JSON.parse(localStorage.getItem('user') || '{}')?.id || null,
          template_id: selectedTemplate.id,
          template_name: selectedTemplate.template_name,
        });
      } catch (e) { /* not critical */ }
    }

    printPrescriptions([{ patient, prescription }], { isAvulsa: true });
    toast.success('Receita gerada e enviada para impressao!');
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        {/* Step 1: Select Template */}
        {step === 'select' && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-xl" data-testid="generate-from-template-title">
                <FileText className="w-5 h-5 text-primary" />
                Gerar Receita Rapida
              </DialogTitle>
              <DialogDescription>
                Selecione um template para gerar a receita do paciente.
              </DialogDescription>
            </DialogHeader>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar template por nome ou doenca..."
                className="pl-10"
                data-testid="template-gen-search"
              />
            </div>

            {loading ? (
              <div className="py-8 text-center text-gray-500">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                Carregando...
              </div>
            ) : templates.length === 0 ? (
              <div className="py-8 text-center text-gray-500">
                <FileText className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                <p>Nenhum template encontrado.</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {templates.map(tmpl => (
                  <div
                    key={tmpl.id}
                    onClick={() => handleSelectTemplate(tmpl)}
                    className="p-4 border rounded-lg hover:border-primary/50 hover:bg-primary/5 transition-all cursor-pointer"
                    data-testid={`gen-template-${tmpl.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h5 className="font-semibold text-gray-900">{tmpl.template_name}</h5>
                        {tmpl.description && (
                          <p className="text-sm text-gray-500 mt-1 line-clamp-1">{tmpl.description}</p>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="secondary" className="text-xs">
                            <Pill className="w-3 h-3 mr-1" />
                            {tmpl.medications?.length || 0} medicamento(s)
                          </Badge>
                          {tmpl.disease_tags?.slice(0, 2).map((tag, i) => (
                            <Badge key={i} variant="outline" className="text-xs bg-blue-50">{tag}</Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end pt-3 border-t">
              <Button variant="ghost" onClick={onClose}>Cancelar</Button>
            </div>
          </>
        )}

        {/* Step 2: Patient Data + Confirm */}
        {step === 'patient' && selectedTemplate && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-xl">
                <Printer className="w-5 h-5 text-primary" />
                Gerar Receita - {selectedTemplate.template_name}
              </DialogTitle>
              <DialogDescription>
                Informe os dados do paciente para gerar a receita.
              </DialogDescription>
            </DialogHeader>

            {!initialTemplate && (
              <Button variant="ghost" size="sm" onClick={() => { setStep('select'); resetForm(); }} className="w-fit">
                <ArrowLeft className="w-4 h-4 mr-1" /> Voltar aos templates
              </Button>
            )}

            {/* Patient Data */}
            <div className="p-4 bg-gray-50 rounded-lg space-y-4">
              <h4 className="font-semibold text-gray-700 flex items-center gap-2">
                <User className="w-4 h-4" />
                Dados do Paciente
              </h4>

              <div>
                <Label>Buscar Paciente Cadastrado (opcional)</Label>
                <PatientSearchAutocomplete
                  value={patientSearch}
                  onChange={setPatientSearch}
                  onSelect={handleSelectPatient}
                  placeholder="Buscar paciente cadastrado..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="gen-patient-name">Nome do Paciente *</Label>
                  <Input
                    id="gen-patient-name"
                    value={patientName}
                    onChange={(e) => setPatientName(e.target.value.toUpperCase())}
                    placeholder="NOME COMPLETO"
                    className="mt-1 h-12 bg-white"
                    data-testid="gen-patient-name"
                  />
                </div>
                <div>
                  <Label htmlFor="gen-patient-cpf">CPF (opcional)</Label>
                  <Input
                    id="gen-patient-cpf"
                    value={patientCPF}
                    onChange={(e) => setPatientCPF(formatCPF(e.target.value))}
                    placeholder="000.000.000-00"
                    maxLength={14}
                    className="mt-1 h-12 bg-white"
                    data-testid="gen-patient-cpf"
                  />
                </div>
              </div>
            </div>

            {/* Medications Preview */}
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-semibold text-blue-800 flex items-center gap-2 mb-3">
                <Pill className="w-4 h-4" />
                Medicamentos do Template ({selectedTemplate.medications?.length || 0})
              </h4>
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {selectedTemplate.medications?.map((med, i) => (
                  <div key={i} className="flex items-start gap-2 p-2 bg-white rounded">
                    <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary flex-shrink-0">
                      {i + 1}
                    </span>
                    <div>
                      <p className="font-semibold text-sm">
                        {med.name} {med.dosage && `${med.dosage}${med.dosage_unit || 'MG'}`}
                      </p>
                      {(med.instructions || med.frequency) && (
                        <p className="text-xs text-gray-600">{med.instructions || med.frequency}</p>
                      )}
                      <div className="flex gap-1 mt-1">
                        {med.quantity && <Badge variant="secondary" className="text-xs">{med.quantity}</Badge>}
                        {med.route && <Badge variant="outline" className="text-xs">{med.route}</Badge>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 justify-between pt-4 border-t">
              <Button variant="outline" onClick={onClose}>Cancelar</Button>
              <Button
                onClick={handlePrint}
                disabled={!patientName.trim()}
                className="bg-primary hover:bg-primary/90"
                data-testid="gen-print-button"
              >
                <Printer className="w-4 h-4 mr-2" />
                Gerar e Imprimir Receita
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
